import { useState } from 'react';
import { descricaoRecurso, type ChaveRecurso, type PerfilEvento } from '@/lib/perfilEvento';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';

interface Props {
  perfil: PerfilEvento;
  definirRecurso: (chave: ChaveRecurso, ligar: boolean) => Promise<void>;
}

function Interruptor({ ligado }: { ligado: boolean }) {
  return (
    <span
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        ligado ? 'bg-primary-500' : 'bg-secondary-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
          ligado ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

export default function MaisRecursos({ perfil, definirRecurso }: Props) {
  const [salvando, setSalvando] = useState<ChaveRecurso | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Esconde o que não se aplica a este tipo de evento — nada de interruptor
  // desligado com explicação comprida.
  const recursos = (perfil.recursos || []).filter((r) => r.disponivel);
  if (recursos.length === 0) return null;

  async function alternar(chave: ChaveRecurso, ligar: boolean) {
    setSalvando(chave);
    setErro(null);
    try {
      await definirRecurso(chave, ligar);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(null);
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
      <div className="px-4 py-3.5">
        <h2 className="text-[15px] font-medium text-foreground-950">Mais recursos</h2>
        <p className="mt-0.5 text-sm text-secondary-500">
          Ligue o que faltar neste evento. Vale na hora.
        </p>
      </div>

      {erro && (
        <div className="mx-4 mb-3 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {erro}
        </div>
      )}

      <div className="divide-y divide-secondary-100 border-t border-secondary-100">
        {recursos.map((r) => {
          const trocando = salvando === r.chave;
          return (
            <div key={r.chave} className="flex items-start gap-3 px-4 py-3.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950">{r.rotulo}</p>
                <p className="mt-0.5 text-xs text-secondary-500">
                  {descricaoRecurso(r.descricao)}
                </p>
              </div>
              <button
                type="button"
                disabled={trocando}
                onClick={() => alternar(r.chave, !r.ligado)}
                aria-label={`${r.ligado ? 'Desligar' : 'Ligar'} ${r.rotulo}`}
                aria-pressed={r.ligado}
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Interruptor ligado={r.ligado} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}